import EditIcon from '@mui/icons-material/Edit'
import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import DatePicker from 'src/components/UI/CustomUI/atoms/DatePicker'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import { ModalActionType } from 'src/components/UI/CustomUI/molecules/Modal/types'
import { jobActions } from 'src/ducks/actions'
import { getJob } from 'src/ducks/selectors'
import { JobItem } from 'src/ducks/types'
import { formatDateTimestamp, formatTimestampDate } from 'src/helpers'
import { useIsLgScreen, useIsXlScreen } from 'src/hooks'
import { Box, Grid, IconButton, LabeledText, TextFieldLabel } from 'src/components/UI'
import { ContentBox, Divider, TabBox } from '../../Templates'
import { formatTimestampUTC } from 'src/helpers'
import { calcWeekends, getHolidaysDate, getDatesBetween } from 'src/helpers/index'
import { addDays } from 'date-fns'
import { SingleDatePicker } from 'src/components/UI/CustomUI/atoms/DatePicker/SingleDatePicker'

interface PropertyAccessForm {
  accessDetails: string
  lockbox: string
  flexibleStartDate: boolean
  preferredStartDate: number | null
  flexibleCompletedOn: boolean
  preferredCompletedOn: number | null
  isOccupied: boolean
  hasChildrenUnderAge: boolean
  hasPets: boolean
}

const PropertyAccess = () => {
  const dispatch = useDispatch()
  const [xlScreen] = useIsXlScreen()
  const [lgScreen] = useIsLgScreen()
  const job = useSelector(getJob())
  const { status, publicStatus } = job || {} as JobItem

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<PropertyAccessForm>({
    accessDetails: '',
    lockbox: '',
    flexibleStartDate: false,
    preferredStartDate: null,
    flexibleCompletedOn: false,
    preferredCompletedOn: null,
    isOccupied: false,
    hasChildrenUnderAge: false,
    hasPets: false,
  })

  const holidays = getHolidaysDate()
  const nextDay = addDays(new Date(), 1)
  const weekends = calcWeekends(new Date(), addDays(new Date(), 400))

  const disabledDates = [...weekends, ...holidays, new Date(), nextDay]

  const handleOpenEdit = () => {
    setForm({
      accessDetails: job?.properties?.accessDetails || '',
      lockbox: job?.properties?.lockbox || '',
      flexibleStartDate: job?.properties?.preferredStartDate == null,
      preferredStartDate: job?.properties?.preferredStartDate || null,
      flexibleCompletedOn: job?.properties?.preferredCompletedOn == null,
      preferredCompletedOn: job?.properties?.preferredCompletedOn || null,
      isOccupied: job?.properties?.isOccupied || false,
      hasChildrenUnderAge: job?.properties?.hasChildrenUnderAge || false,
      hasPets: job?.properties?.hasPets || false,
    })
    setOpen(true)
  }

  const handleFlexibleStartDate = (flexStartDate: boolean) => {
    if (flexStartDate) {
      setForm({ ...form, flexibleStartDate: flexStartDate, preferredStartDate: null })
    } else {
      setForm({ ...form, flexibleStartDate: flexStartDate})
    }
  }

  const handleFlexibleCompletedOn = (flexCompletedOn: boolean) => {
    if (flexCompletedOn) {
      setForm({ ...form, flexibleCompletedOn: flexCompletedOn, preferredCompletedOn: null })
    } else {
      setForm({ ...form, flexibleCompletedOn: flexCompletedOn})
    }
  }

  const handleSave = () => {
    setSaving(true)
    dispatch(
      jobActions.updateJobProperty(
        {
          accessDetails: form.accessDetails,
          lockbox: form.lockbox,
          preferredStartDate: form.preferredStartDate as number,
          preferredCompletedOn: form.preferredCompletedOn as number,
          isOccupied: form.isOccupied,
          hasChildrenUnderAge: form.hasChildrenUnderAge,
          hasPets: form.hasPets,
        },
        (succ: boolean) => {
          setSaving(false)
          if (succ) {
            toast.success.call('success', 'Information & Access updated successfully')
            setOpen(false)
          }
        }
      )
    )
  }

  const modalActions: ModalActionType[] = [
    {
      textButton: 'Cancel',
      variant: 'outlined',
      onClick: () => {
        setOpen(false)
      },
    },
    {
      textButton: 'Save',
      variant: 'contained',
      onClick: handleSave,
      disabled: saving,
      loading: saving,
    },
  ]

  return (
    <>
      <TabBox>
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='Client Name:' textValue={job?.properties?.clientName || ''} />
          <LabeledText labelValue='Channel:' textValue={job?.properties?.affiliateType || ''} />
          <LabeledText labelValue='Job Status:' textValue={publicStatus?.toUpperCase() === "HOLD" ? "HOLD" : status ? (status.startsWith('V2_') ? status.slice(3) : status) : ''} />
        </ContentBox>
        <Divider />
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='Access Info:' textValue={job?.properties?.accessDetails || ''} />
          <LabeledText labelValue='Lockbox code:' textValue={job?.properties?.lockbox || ''} />
        </ContentBox>
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='Preferred Start Date:' textValue={formatTimestampUTC(job?.properties?.preferredStartDate, 'MM/DD/YYYY')} />
          <LabeledText labelValue='Preferred Completion Date:' textValue={formatTimestampUTC(job?.properties?.preferredCompletedOn, 'MM/DD/YYYY')} />
        </ContentBox>
        <Divider />
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='Occupancy:' textValue={job?.properties?.isOccupied ? 'Yes' : 'No'} />
          <LabeledText labelValue='Children Under 18:' textValue={job?.properties?.hasChildrenUnderAge ? 'Yes' : 'No'} />
          <LabeledText labelValue='Pets:' textValue={job?.properties?.hasPets ? 'Yes' : 'No'} />
        </ContentBox>
        <Divider />
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='Custom Flag:' textValue={''} />
          <LabeledText labelValue='Property Type:' textValue={''} />
          <LabeledText labelValue='Promo:' textValue={''} />
        </ContentBox>
      </TabBox>

      <Box sx={{ position: 'absolute', bottom: '12px', right: '12px' }}>
        <IconButton size="small" onClick={handleOpenEdit}>
          <EditIcon />
        </IconButton>
      </Box>

      <Modal
        setOpen={setOpen}
        open={open}
        title='Edit Information & Access'
        actions={modalActions}
        onClose={() => setOpen(false)}
        size='sm'
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextFieldLabel
              label='Access Info:'
              labelVariant='h6'
              value={form.accessDetails}
              onChange={(e) => setForm({ ...form, accessDetails: e.target.value })}
              isTextArea
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldLabel
              label='Lockbox code:'
              labelVariant='h6'
              value={form.lockbox}
              onChange={(e) => setForm({ ...form, lockbox: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <LabeledText labelValue='Preferred Start Date:' />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DatePicker
                minDate={new Date()}
                value={form.preferredStartDate ? formatTimestampDate(form.preferredStartDate) : null}
                onChange={(date: Date | null) =>
                  setForm({ ...form, preferredStartDate: date ? formatDateTimestamp(date) : null })
                }
                disabled={form.flexibleStartDate}
                disabledDates={disabledDates}
                size='small'
                style={{ height: '30px', padding: '0px', width: '300px' }}
              />
              <Checkbox
                checked={form.flexibleStartDate}
                onChange={() => handleFlexibleStartDate(!form.flexibleStartDate)}
                name="flexible"
                color="primary"
                style={{ padding: '0px', height: 'fit-content' }}
              />
              <Typography sx={{ fontWeight: 'bold' }}>
                Flexible
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <LabeledText labelValue='Preferred Completion Date:' />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DatePicker
                value={form.preferredCompletedOn ? formatTimestampDate(form.preferredCompletedOn) : null}
                onChange={(date: Date | null) =>
                  setForm({ ...form, preferredCompletedOn: date ? formatDateTimestamp(date) : null })
                }
                disabled={form.flexibleCompletedOn}
                disabledDates={[...disabledDates, ...getDatesBetween(addDays(new Date(), -37000), form.preferredStartDate ? formatTimestampDate(form.preferredStartDate) : new Date())]}
                size='small'
                style={{ height: '30px', padding: '0px', width: '300px' }}
              />
              <Checkbox
                checked={form.flexibleCompletedOn}
                onChange={() => handleFlexibleCompletedOn(!form.flexibleCompletedOn)}
                name="flexible"
                color="primary"
                style={{ padding: '0px', height: 'fit-content' }}
              />
              <Typography sx={{ fontWeight: 'bold' }}>
                Flexible
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h6'>Property Details:</Typography>
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isOccupied}
                  onChange={() => setForm({ ...form, isOccupied: !form.isOccupied })}
                  color='infoText'
                />
              }
              label={<Typography variant='body2'>Occupancy</Typography>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.hasChildrenUnderAge}
                  onChange={() => setForm({ ...form, hasChildrenUnderAge: !form.hasChildrenUnderAge })}
                  color='infoText'
                />
              }
              label={<Typography variant='body2'>Children Under 18</Typography>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.hasPets}
                  onChange={() => setForm({ ...form, hasPets: !form.hasPets })}
                  color='infoText'
                />
              }
              label={<Typography variant='body2'>Pets</Typography>}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

export default PropertyAccess
